/**
 * QA-356 — 이벤트 관리 검색 조건(이벤트명·기간·진행 상태·활성 여부·당첨자 발표). BE 목록이 검색을 받지 않아
 * 화면에서 거른다.
 */

export type EventPeriodStatus = 'SCHEDULED' | 'ONGOING' | 'ENDED' | 'UNSET';

export interface EventListRow {
  eventId: string;
  title: string;
  startDate: string;
  endDate: string;
  sortOrder: number;
  isActive: boolean;
  isWinnersPublished: boolean;
}

export interface EventListFilter {
  /** 이벤트명 부분일치(대소문자·앞뒤 공백 무시). */
  keyword: string;
  /** 기간 — 이벤트 기간이 이 범위와 겹치면 포함(yyyy-MM-dd, 비우면 제한 없음). */
  from: string;
  to: string;
  status: 'ALL' | EventPeriodStatus;
  active: 'ALL' | 'ACTIVE' | 'INACTIVE';
  winners: 'ALL' | 'ANNOUNCED' | 'NOT_ANNOUNCED';
}

export const EMPTY_EVENT_FILTER: EventListFilter = {
  keyword: '',
  from: '',
  to: '',
  status: 'ALL',
  active: 'ALL',
  winners: 'ALL',
};

/** 오늘(KST) 기준 진행 상태 — 목록 "진행상태" 배지와 같은 기준. */
export const eventPeriodStatus = (startDate: string, endDate: string, today: string): EventPeriodStatus => {
  if (!startDate || !endDate) return 'UNSET';
  if (today < startDate) return 'SCHEDULED';
  if (today > endDate) return 'ENDED';
  return 'ONGOING';
};

export const todayKst = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export const filterEvents = <T extends EventListRow>(rows: ReadonlyArray<T>, f: EventListFilter, today: string): T[] => {
  const kw = f.keyword.trim().toLowerCase();
  return rows.filter((r) => {
    if (kw && !r.title.toLowerCase().includes(kw)) return false;
    // 기간 겹침: 이벤트 [start,end] 와 검색 [from,to].
    if (f.from && r.endDate && r.endDate < f.from) return false;
    if (f.to && r.startDate && r.startDate > f.to) return false;
    if (f.status !== 'ALL' && eventPeriodStatus(r.startDate, r.endDate, today) !== f.status) return false;
    if (f.active !== 'ALL' && r.isActive !== (f.active === 'ACTIVE')) return false;
    if (f.winners !== 'ALL' && r.isWinnersPublished !== (f.winners === 'ANNOUNCED')) return false;
    return true;
  });
};

/**
 * QA-365 — 저장용 status. 진행 상태는 시작일·종료일(KST)로 정해지므로 폼에서 고르지 않고 저장 시 도출한다.
 * BE 는 status 를 필수로 받고, 상품 상세의 "연결된 진행중 이벤트"가 아직 저장값을 쓴다.
 */
export const deriveEventStatus = (
  startDate: string,
  endDate: string,
  today: string,
): 'UPCOMING' | 'ONGOING' | 'ENDED' => {
  const s = eventPeriodStatus(startDate, endDate, today);
  return s === 'ONGOING' ? 'ONGOING' : s === 'ENDED' ? 'ENDED' : 'UPCOMING';
};

/**
 * QA-365 — 새 이벤트의 sortOrder. 공개 목록이 "진행중 우선 → sortOrder 큰 순(=등록 역순)"이라 기존 최댓값 + 1 을
 * 준다(수기 입력을 없앴다. 종전엔 비워 두면 0 이라 목록 맨 뒤로 갔다).
 */
export const nextEventSortOrder = (rows: ReadonlyArray<{ sortOrder: number }>): number =>
  rows.reduce((max, r) => Math.max(max, Number.isFinite(r.sortOrder) ? r.sortOrder : 0), 0) + 1;

export type EventSortKey = 'title' | 'period' | 'periodStatus' | 'isActive' | 'isWinnersPublished';
export interface EventSort {
  key: EventSortKey;
  direction: 'asc' | 'desc';
}

const STATUS_RANK: Record<EventPeriodStatus, number> = { ONGOING: 0, SCHEDULED: 1, ENDED: 2, UNSET: 3 };

/** 기간 최근 순 — 시작일, 같으면 종료일. 빈 날짜는 뒤로. */
const byPeriodDesc = (a: EventListRow, b: EventListRow): number => {
  if (a.startDate !== b.startDate) {
    if (!a.startDate) return 1;
    if (!b.startDate) return -1;
    return a.startDate < b.startDate ? 1 : -1;
  }
  if (a.endDate !== b.endDate) {
    if (!a.endDate) return 1;
    if (!b.endDate) return -1;
    return a.endDate < b.endDate ? 1 : -1;
  }
  return b.sortOrder - a.sortOrder;
};

/**
 * QA-500 — 등록 번호(No). 열 정렬·검색과 상관없이 이벤트마다 고정된 번호다. 등록 순서(sortOrder, 새 이벤트는
 * 최댓값+1 — {@link nextEventSortOrder})로 1부터 매기고, sortOrder 가 같으면 받은 순서(BE sortOrder 오름차순)를 따른다.
 */
export const eventRegistrationNumbers = (rows: ReadonlyArray<EventListRow>): Map<string, number> => {
  const ordered = rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => a.r.sortOrder - b.r.sortOrder || a.i - b.i);
  return new Map(ordered.map(({ r }, n) => [r.eventId, n + 1]));
};

/**
 * 목록 정렬. QA-500 — 기본(sort=null)은 **최신 등록 순**(sortOrder 큰 순)으로, FE 이벤트 목록 "최신순"과 같은 기준이다.
 * 열 머리글을 누르면 그 열로 오름/내림차순(QA-365), 같은 값끼리는 진행상태(진행중 → 예정 → 종료) → 기간 순.
 */
export const sortEvents = <T extends EventListRow>(rows: ReadonlyArray<T>, sort: EventSort | null, today: string): T[] => {
  const rank = (r: T) => STATUS_RANK[eventPeriodStatus(r.startDate, r.endDate, today)];
  const base = (a: T, b: T) => rank(a) - rank(b) || byPeriodDesc(a, b);
  if (!sort) {
    const numbers = eventRegistrationNumbers(rows);
    return [...rows].sort((a, b) => (numbers.get(b.eventId) ?? 0) - (numbers.get(a.eventId) ?? 0));
  }
  const sign = sort.direction === 'asc' ? 1 : -1;
  const primary = (a: T, b: T): number => {
    switch (sort.key) {
      case 'title':
        return a.title.localeCompare(b.title, 'ko');
      case 'period':
        return -byPeriodDesc(a, b);
      case 'periodStatus':
        return rank(a) - rank(b);
      case 'isActive':
        return Number(b.isActive) - Number(a.isActive);
      case 'isWinnersPublished':
        return Number(b.isWinnersPublished) - Number(a.isWinnersPublished);
    }
  };
  return [...rows].sort((a, b) => sign * primary(a, b) || base(a, b));
};
